import {
  MAX_CANVAS_SIZE,
  CARD_WIDTH_PCT,
  CARD_RATIO,
  OCR_LEFT_MARGIN_PCT,
  OCR_HEIGHT_PCT,
  QR_ZONE_SIZE_PCT,
  BORDER_SHIFT_PCT,
} from "./cardGeometry";

/**
 * Crop image to the card region (same as camera overlay: center 90% width, height = width/ratio).
 */
export function cropToCardRegion(dataUri: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const cw = Math.round(w * CARD_WIDTH_PCT);
      const ch = Math.round(cw / CARD_RATIO);
      const x = Math.round((w - cw) / 2);
      const y = Math.round((h - ch) / 2);
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUri);
        return;
      }
      ctx.drawImage(img, x, y, cw, ch, 0, 0, cw, ch);
      try {
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      } catch {
        resolve(dataUri);
      }
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

/**
 * Crop image to the QR zone only: bottom-right square of the card (same as green overlay).
 */
export function cropToQrRegion(dataUri: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const cw = Math.round(w * CARD_WIDTH_PCT);
      const ch = Math.round(cw / CARD_RATIO);
      const cardX = Math.round((w - cw) / 2);
      const cardY = Math.round((h - ch) / 2);
      const qrSize = Math.round(ch * QR_ZONE_SIZE_PCT);
      const qrExtension = Math.round(cw * BORDER_SHIFT_PCT);
      const qrX = cardX + cw - qrSize - qrExtension;
      const qrY = cardY + ch - qrSize;
      const qrW = qrSize + qrExtension;
      if (qrSize <= 0 || qrW <= 0) {
        resolve(dataUri);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = qrW;
      canvas.height = qrSize;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUri);
        return;
      }
      ctx.drawImage(img, qrX, qrY, qrW, qrSize, 0, 0, qrW, qrSize);
      try {
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      } catch {
        resolve(dataUri);
      }
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

/**
 * Crop image to the OCR zone only: bottom half of card, from left margin to just left of QR zone.
 * Only this region is sent to OCR.
 */
export function cropToOcrRegion(dataUri: string): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const cw = Math.round(w * CARD_WIDTH_PCT);
      const ch = Math.round(cw / CARD_RATIO);
      const cardX = Math.round((w - cw) / 2);
      const cardY = Math.round((h - ch) / 2);
      const qrSize = Math.round(ch * QR_ZONE_SIZE_PCT);
      const qrExtension = Math.round(cw * BORDER_SHIFT_PCT);
      const leftMargin = Math.round(cw * OCR_LEFT_MARGIN_PCT);
      const ocrH = Math.round(ch * OCR_HEIGHT_PCT);
      const ocrX = cardX + leftMargin;
      const ocrY = cardY + ch - ocrH;
      const ocrW = cw - leftMargin - qrSize - qrExtension;
      if (ocrW <= 0 || ocrH <= 0) {
        resolve(dataUri);
        return;
      }
      const canvas = document.createElement("canvas");
      canvas.width = ocrW;
      canvas.height = ocrH;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUri);
        return;
      }
      ctx.drawImage(img, ocrX, ocrY, ocrW, ocrH, 0, 0, ocrW, ocrH);
      try {
        resolve(canvas.toDataURL("image/jpeg", 0.95));
      } catch {
        resolve(dataUri);
      }
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}

/**
 * Resize image to max 1920px for OCR/QR, return as data URL.
 */
export function preprocessImage(dataUri: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const w = img.width;
      const h = img.height;
      const scale = Math.min(1, MAX_CANVAS_SIZE / Math.max(w, h));
      const cw = Math.round(w * scale);
      const ch = Math.round(h * scale);
      const canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUri);
        return;
      }
      ctx.drawImage(img, 0, 0, cw, ch);
      try {
        const out = canvas.toDataURL("image/jpeg", 0.95);
        resolve(out);
      } catch {
        resolve(dataUri);
      }
    };
    img.onerror = () => resolve(dataUri);
    img.src = dataUri;
  });
}
